import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import { TextField, Tooltip } from '@mui/material'
import React from 'react'
import { LookupField } from '../../../components/filter-fields'
import { listSearchSx } from '../../../components/list-styles'
import { ClipText, ReportKpi } from '../../../components/report-parts'
import ReportTable from '../../../components/ReportTable'
import type { ReportTableColumn } from '../../../components/ReportTable'
import KarteraAdminTemplate from '../../../template/KarteraAdminTemplate'
import KarteraPageHeader from '../../../template/kartera/KarteraPageHeader'
import { searchClientes } from '../../../utils/lookups'
import CustomerDrawer from '../../customers/drawer/customer-drawer'
import { formatCount, PAGE_SIZES } from '../common/report-common'
import { useReportCustomersModel } from './report-customers-model'
import type { CustomerReportRow } from './report-customers-model'

const ReportCustomers = () => {
    const model = useReportCustomersModel()

    const columns: ReportTableColumn<CustomerReportRow>[] = [
        {
            key: 'ver',
            label: '',
            render: (row) => (
                <Tooltip title="Ver cliente">
                    <button type="button" aria-label={`Ver ${row.nombre}`} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent text-k-primary-ink hover:bg-k-accent-soft" onClick={() => model.openCliente(row.id)}>
                        <VisibilityOutlinedIcon style={{ fontSize: 18 }} />
                    </button>
                </Tooltip>
            ),
        },
        {
            key: 'nombre',
            label: 'Cliente',
            minWidth: 220,
            render: (row) => (
                <span className="flex items-center gap-1.5">
                    {row.nombre ? (
                        <Tooltip title={row.nombre} placement="top-start">
                            <span className="block max-w-[240px] truncate font-bold text-k-ink">{row.nombre}</span>
                        </Tooltip>
                    ) : <span className="text-k-muted">—</span>}
                    {row.robot && (
                        <Tooltip title="Creado por la sincronización automática">
                            <SmartToyOutlinedIcon className="shrink-0 text-tone-orange-ink" style={{ fontSize: 16 }} />
                        </Tooltip>
                    )}
                </span>
            ),
        },
        { key: 'rfc', label: 'RFC', render: (row) => <span className="whitespace-nowrap tabular-nums text-k-ink-2">{row.rfc || '—'}</span> },
        { key: 'sexo', label: 'Género', render: (row) => <span className="text-k-ink-2">{row.sexo || '—'}</span> },
        { key: 'nacimiento', label: 'Nacimiento', render: (row) => <span className="whitespace-nowrap tabular-nums text-k-ink-2">{row.nacimiento || '—'}</span> },
        { key: 'telefono', label: 'Teléfono', render: (row) => <span className="whitespace-nowrap tabular-nums text-k-ink-2">{row.telefono || '—'}</span> },
        { key: 'correo', label: 'Correo', minWidth: 200, render: (row) => <ClipText text={row.correo} /> },
        {
            key: 'polizas',
            label: 'Pólizas',
            align: 'right',
            render: (row) => (
                <span className={`rounded-full px-[9px] py-[3px] text-xs font-bold ${row.polizas > 0 ? 'bg-k-accent-soft text-k-accent-ink' : 'bg-k-line-soft text-k-muted'}`}>
                    {formatCount(row.polizas)}
                </span>
            ),
        },
        { key: 'creacion', label: 'Creación', render: (row) => <span className="whitespace-nowrap tabular-nums text-k-muted">{row.creacion}</span> },
    ]

    return (
        <KarteraAdminTemplate loading={false} title="Reporte de clientes">
            <CustomerDrawer clienteId={model.detailId} onClose={model.closeCliente} onEdit={model.editCliente} />

            <div className="mx-auto flex w-full max-w-[1280px] flex-col gap-[18px]">
                <KarteraPageHeader
                    title="Reporte de clientes"
                    description="Clientes de tu cartera con sus datos de contacto y el número de pólizas."
                    actions={(
                        <button
                            type="button"
                            className="inline-flex h-11 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover"
                            onClick={model.reload}
                        >
                            <RefreshIcon className={model.loading ? 'animate-spin' : ''} style={{ fontSize: 18 }} />
                            Actualizar
                        </button>
                    )}
                />

                <section className="k-card gap-3">
                    <div className="flex flex-wrap items-start gap-2.5">
                        <LookupField label="Cliente" value={model.cliente} search={searchClientes} onChange={model.changeCliente} />
                    </div>
                    <p className="m-0 text-xs text-k-muted">Elige un cliente para ver solo su registro; déjalo vacío para ver toda la cartera.</p>
                </section>

                {model.error && (
                    <div className="flex items-center gap-3 rounded-[14px] bg-k-danger-soft px-4 py-3.5 text-sm text-k-danger-ink">
                        <ErrorOutlineIcon style={{ fontSize: 20 }} />
                        <p className="m-0">{model.error}</p>
                    </div>
                )}

                <div className={`grid grid-cols-2 gap-3 lg:grid-cols-4 ${model.loading ? 'opacity-60' : ''}`}>
                    <ReportKpi label="Clientes" value={formatCount(model.count)} />
                    <ReportKpi label="Con pólizas" value={formatCount(model.withPolicies)} />
                    <ReportKpi label="Sin pólizas" value={formatCount(model.withoutPolicies)} />
                    <ReportKpi label="Por sincronización" value={formatCount(model.robots)} hint="Creados automáticamente" />
                </div>

                <ReportTable<CustomerReportRow>
                    columns={columns}
                    rows={model.rows}
                    rowKey={(row) => row.id}
                    emptyText={model.loading ? 'Cargando clientes…' : 'No hay clientes con los filtros aplicados.'}
                    loading={model.loading}
                    page={model.page}
                    totalPages={model.totalPages}
                    pages={model.pages}
                    pageSize={model.pageSize}
                    pageSizes={PAGE_SIZES}
                    rangeFrom={model.rangeFrom}
                    rangeTo={model.rangeTo}
                    totalRows={model.filteredCount}
                    noun="clientes"
                    formatCount={formatCount}
                    onPage={model.setPage}
                    onPageSize={model.changePageSize}
                    toolbar={(
                        <>
                            <div className="min-w-0 flex-1">
                                <TextField
                                    label="Buscar por nombre, RFC, correo o teléfono"
                                    size="small"
                                    value={model.search}
                                    sx={listSearchSx}
                                    slotProps={{ htmlInput: { autoComplete: 'off' } }}
                                    onChange={(event) => model.changeSearch(event.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                disabled={model.filteredCount === 0}
                                onClick={model.exportCsv}
                                className="inline-flex h-10 items-center gap-2 rounded-xl border border-k-line bg-k-surface px-4 text-sm font-semibold text-k-ink hover:bg-k-hover disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <DownloadOutlinedIcon style={{ fontSize: 18 }} />
                                Exportar CSV
                            </button>
                        </>
                    )}
                />
            </div>
        </KarteraAdminTemplate>
    )
}

export default ReportCustomers
